import { useState } from 'preact/hooks'
import type { CompanyProfileDto } from '@/api/companies'
import { companiesApi } from '@/api/companies'
import { useToast } from '@/hooks/useToast'
import { useUserProfile } from '@/hooks/useUserProfile'

interface Props {
  profile: CompanyProfileDto
  clientCompanyId: string
  workspaceId: string
  onLinked: () => void
}

/**
 * Pipeline credited to this company, and the affordance to say so when nobody could work it out.
 *
 * CRM deals are attributed by email domain and never by our company id, so the machine links a
 * domain only when it names exactly one company. Several — a parent and a subsidiary — or none
 * both leave it unlinked, and someone has to decide. Confirming writes only to this tenant's own
 * attribution records; it never edits the shared global company.
 */
export function CompanyPipelineBand({ profile, clientCompanyId, workspaceId, onLinked }: Props) {
  const pipeline = profile.Pipeline
  const { showSuccess, showErrorUnless422 } = useToast()
  const { getWorkspaceCapabilities } = useUserProfile()
  const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId)
  const [confirming, setConfirming] = useState(false)

  // Nothing to say: no CRM connected, or no deal matched this company. A zero here would read as
  // "we sold them nothing", which is a different claim from "we don't know".
  if (!pipeline) return null

  // The domains that actually carried deals, from the server — not EmailDomains[0]. A company
  // known by acme.com and acme.co.uk whose deals all came through the first must not have the
  // second confirmed on its behalf, since confirming writes a tenant-wide attribution.
  const domains = pipeline.MatchedDomains
  const domainLabel = domains.length === 1 ? domains[0] : `${domains.length} domains`
  const canConfirm = !pipeline.IsLinked && canEdit && domains.length > 0

  const confirm = async () => {
    if (domains.length === 0) return
    setConfirming(true)
    try {
      const res = await companiesApi.confirmCrmLink(clientCompanyId, workspaceId, profile.CompanyId, domains)
      if (res.error) {
        showErrorUnless422('Could not link CRM account', res)
        return
      }
      showSuccess('CRM account linked', `${domains.join(', ')} now credits pipeline to ${profile.Name || 'this company'}.`)
      onLinked()
    } finally {
      // In a finally, so a network error cannot leave the button reading "Linking…" forever.
      setConfirming(false)
    }
  }

  return (
    <div class="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3 border-y border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
      <Figure
        value={formatMoney(pipeline.PipelineGeneratedTotal, pipeline.Currency)}
        label={`pipeline generated${pipeline.PipelineGeneratedCount ? ` · ${pipeline.PipelineGeneratedCount} deal${pipeline.PipelineGeneratedCount === 1 ? '' : 's'}` : ''}`}
      />
      <Figure
        value={formatMoney(pipeline.ClosedWonTotal, pipeline.Currency)}
        label="closed won"
      />

      <div class="ml-auto flex items-center gap-2 text-xs">
        {pipeline.IsLinked ? (
          <span
            class="inline-flex items-center gap-1.5 font-medium text-primary-700 dark:text-primary-400"
            title={
              pipeline.LinkSource === 'Confirmed'
                ? 'Someone confirmed this CRM account belongs to this company'
                : 'Matched automatically — the domain named exactly one company'
            }
          >
            <span aria-hidden="true">✓</span>
            CRM account linked
          </span>
        ) : (
          <>
            <span class="text-slate-500 dark:text-slate-400" title={domains.join(', ')}>
              Matched on {domainLabel} — not confirmed
            </span>
            {canConfirm && (
              <button
                type="button"
                onClick={confirm}
                disabled={confirming}
                class="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-2xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {confirming ? 'Linking…' : 'Confirm'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function Figure({ value, label }: { value: string; label: string }) {
  return (
    <div class="flex items-baseline gap-1.5">
      <span class="text-[15px] font-semibold text-slate-900 dark:text-slate-100 tabular-nums">{value}</span>
      <span class="text-xs text-slate-500 dark:text-slate-400">{label}</span>
    </div>
  )
}

/** No currency means the deals behind the figure disagreed, so the symbol would be a lie. */
function formatMoney(amount: number, currency?: string | null): string {
  if (!currency) return Math.round(amount).toLocaleString()
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount)
  } catch {
    return `${Math.round(amount).toLocaleString()} ${currency}`
  }
}
